import React, { useState } from 'react';
import { ProductImage } from '../../types';
import './ImageGallery.css';

interface ImageGalleryProps {
  images: ProductImage[];
  productName: string;
  preferredImage?: string | null;
  youtubeEmbedUrl?: string | null;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, productName, preferredImage, youtubeEmbedUrl }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const safeImages = React.useMemo(() => images || [], [images]);
  const activeIndex = safeImages.length ? Math.min(selectedImage, safeImages.length - 1) : 0;
  const activeImage = safeImages[activeIndex];

  React.useEffect(() => {
    if (!safeImages.length) {
      setSelectedImage(0);
      return;
    }
    if (preferredImage) {
      const index = safeImages.findIndex(x => x.image === preferredImage);
      if (index >= 0) {
        setSelectedImage(index);
        return;
      }
    }
    setSelectedImage(current => Math.min(current, safeImages.length - 1));
  }, [preferredImage, safeImages]);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev === 0 ? safeImages.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev === safeImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="image-gallery">
      {safeImages.length > 1 && (
        <div className="thumbnail-list">
          {safeImages.map((image, index) => (
            <div
              key={image.id}
              className={`thumbnail ${index === activeIndex ? 'active' : ''}`}
              onClick={() => setSelectedImage(index)}
            >
              <img src={image.image} alt={`${productName} thumbnail ${index + 1}`} />
            </div>
          ))}
        </div>
      )}

      <div className="main-image-container">
        <div className="main-image">
          {activeImage ? (
            <img
              src={activeImage.image}
              alt={`${productName} - ${activeIndex + 1}`}
            />
          ) : (
            <div className="image-gallery-empty">{productName}</div>
          )}
        </div>

        {safeImages.length > 1 && (
          <>
            <button className="nav-btn prev" onClick={handlePrev} aria-label="Previous image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button className="nav-btn next" onClick={handleNext} aria-label="Next image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </>
        )}

        {youtubeEmbedUrl && (
          <div className="product-video-under-photo">
            <h3 className="video-under-photo-title">Product Video</h3>
            <div className="video-frame-wrapper">
              <iframe 
                title={`${productName} Video`} 
                src={youtubeEmbedUrl} 
                loading="lazy" 
                allowFullScreen 
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
